import { db, storage } from "../firebase-config";
import { getDoc, updateDoc, onSnapshot, deleteDoc, doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { fetchAndCacheImage } from "../utils/cache/ImageCache";
import { UserStatus } from "../components/routes/routing_statuses/RoutingStatuses";

export const onUserSnapshot = (uid, onUserUpdate, { setFirebaseUser, setUserRole, setThemeMode } = {}) => {
  if(uid) {
    try {
      const userPathRef = doc(db, "users", uid);
      const unsubscribeUser = onSnapshot(userPathRef, async (getUserOrgDoc) => {
        if (!getUserOrgDoc.exists()) {
          // If using direct state setters
          if (setFirebaseUser && setUserRole) {
            setFirebaseUser(UserStatus.DOES_NOT_EXIST);
            setUserRole(null);
          }
          // Still support callback approach for backward compatibility
          if (onUserUpdate) {
            onUserUpdate(UserStatus.DOES_NOT_EXIST);
          }
          return;
        }  
        const organizations = getUserOrgDoc.data().organizations;  
        const firstOrganization = organizations[0]; 
        const userInOrgPath = doc(db, "organizations", firstOrganization, "employees", uid);
        const unsubscribeOrgUser = onSnapshot(userInOrgPath, async (userInOrgDoc) => {
          if (!userInOrgDoc.exists()) return;
          let combinedUserData = {
            ...getUserOrgDoc.data(),
            ...userInOrgDoc.data(),
            organization: firstOrganization,
            uid: userInOrgDoc.id,
          };
  
          if (combinedUserData.profile_picture_url) {
            const cachedImageUrl = await fetchAndCacheImage(combinedUserData.profile_picture_url);
            combinedUserData.profile_picture_url = cachedImageUrl;
          }

          // If using direct state setters
          if (setFirebaseUser && setUserRole) {
            setFirebaseUser(combinedUserData);
            setUserRole(combinedUserData.role);
            if (setThemeMode && combinedUserData.theme) {
              setThemeMode(combinedUserData.theme);
            }
          }
          
          // Still support callback approach for backward compatibility
          if (onUserUpdate) {
            onUserUpdate(combinedUserData);
          }
        });
        return unsubscribeOrgUser;
      });
  
      return unsubscribeUser;
    } catch (error) {
      return;
    }
  }
};

export const updateUserTheme = async (userId, theme) => {
    try {
        const userDocRef = doc(db, "users", userId);
        await updateDoc(userDocRef, { theme });
        return { success: true, error: null };
    } catch (error) {
        console.log(error);
        return { success: false, error: error.message };
    }
};

export const updateProfilePicture = async (organization, userId, file) => {
    try { 
        const storageRef = ref(storage, `organizations/${organization}/employees/${userId}/profile_picture/image`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        const userDocRef = doc(db, "organizations", organization, "employees", userId);
        await updateDoc(userDocRef, { profile_picture_url: downloadURL });
        return { success: true, data: downloadURL, error: null };
    } catch(error) {
        console.log(error);
        return { success: false, data: null, error: error.message };
    }
};

export const getOrganizationUser = async(organization, uid) => {
    try {
        var docRef = doc(db, "organizations", organization, "employess", uid);
        return await getDoc(docRef)
        } catch(error) {
            console.log(error)
        }
} 

export const updateEmployee = async (organization, uid, employeeData) => {
    try {
        const employeeRef = doc(db, "organizations", organization, "employees", uid);
        await updateDoc(employeeRef, employeeData);
        return { success: true, error: null };
    } catch (error) {
        console.log(error);
        return { success: false, error: error.message };
    }
};

export const deleteEmployee = async (organization, employeeId) => {
  try {
      const employeeRef = doc(db, "organizations", organization, "employees", employeeId);
      await deleteDoc(employeeRef);
      return { success: true, error: null };
  } catch (error) {
      console.error("Error deleting employee:", error);
      return { success: false, error: error.message || 'Failed to delete employee' };
  }
};

export const updateEmployeeAvailability = async (organization, uid, availability) => {
    try {
        const employeeRef = doc(db, "organizations", organization, "employees", uid);
        await updateDoc(employeeRef, availability);
        return { success: true, error: null };
    } catch (error) {
        console.log(error);
        return { success: false, error: error.message };
    }
};

export const storeFCMToken = async (userId, token) => {
  try {
    // Get the current user document
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      let fcmTokens = userData.fcm_tokens || [];
      
      // Check if token already exists to avoid duplicates
      if (!fcmTokens.includes(token)) {
        // Add the new token to the array
        fcmTokens.push(token);
        
        // Update the document with the new tokens array
        await updateDoc(userRef, {
          fcm_tokens: fcmTokens
        });
        
        console.log(`FCM token added to user's tokens array`);
      } else {
        console.log(`FCM token already exists for this user`);
      }
    } else {
      // Create new user document with the token in an array
      await setDoc(userRef, {
        fcm_tokens: [token]
      });
      console.log(`New user document created with FCM token`);
    }
    
    return { success: true, error: null };
  } catch (error) {
    console.error("Error storing FCM token:", error);
    return { success: false, error: error.message };
  }
};

export const removeFCMToken = async (userId, token) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      let fcmTokens = userData.fcm_tokens || [];
      
      // Remove the token from the array
      const updatedTokens = fcmTokens.filter(t => t !== token);
      
      // Update the document with the filtered tokens array
      await updateDoc(userRef, {
        fcm_tokens: updatedTokens
      });
      
      console.log(`FCM token removed from user's tokens array`);
    }
    
    return { success: true, error: null };
  } catch (error) {
    console.error("Error removing FCM token:", error);
    return { success: false, error: error.message };
  }
};
